import React from 'react';
import { motion } from 'framer-motion';
import { IconWrapper } from '../../components/common/IconWrapper';
import {
  HiStar,
  HiUsers,
  HiBuildingOffice,
  HiMiniSparkles,
  HiCheckCircle,
  HiGlobeAlt
} from 'react-icons/hi2';

const heroImage = 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1200&q=60';

const keyMetrics = [
  { metric: 'Pages migrated', value: '50+' },
  { metric: 'Database queries optimized', value: '200+' },
  { metric: 'Uptime achieved', value: '99.9%' }
];

const results = [
  { metric: 'Page Load Time', value: '-70%' },
  { metric: 'User Engagement', value: '+85%' },
  { metric: 'Database Performance', value: '+90%' },
  { metric: 'Cost Efficiency', value: '60%' }
];

const painPoints = [
  'Legacy codebase with outdated technologies causing slow page loads affecting product catalog visibility',
  'Database performance issues leading to poor user experience for customers browsing electrical conduit products',
  'Limited scalability and difficulty in adding new features for product management and catalog updates',
  'High maintenance costs and security vulnerabilities impacting online business operations'
];

const strategy = [
  'Migrated entire application to modern Node.js and React architecture for better performance and maintainability',
  'Optimized MySQL database with proper indexing and query optimization for faster product catalog retrieval',
  'Implemented responsive design for mobile and desktop compatibility to reach customers across all devices',
  'Deployed on shared hosting (technoflex.in) with optimized configurations for cost-effective hosting solution'
];

const outcomes = [
  { title: 'Performance Boost', detail: 'Page load times reduced by 70%, significantly improving user experience for customers browsing electrical conduit products and enhancing SEO rankings' },
  { title: 'Scalable Architecture', detail: 'Modern stack enables easy product catalog management, feature additions, and future scalability without major refactoring' },
  { title: 'Cost Optimization', detail: 'Shared hosting deployment at technoflex.in reduced infrastructure costs by 60% while maintaining high performance for manufacturing business operations' }
];

const timeline = [
  { phase: 'Analysis & Planning', duration: '2 weeks', focus: 'Legacy code review and migration strategy' },
  { phase: 'Development', duration: '8 weeks', focus: 'Node.js backend, React frontend, and database optimization' },
  { phase: 'Testing & Deployment', duration: '2 weeks', focus: 'Quality assurance and deployment to technoflex.in' }
];

const technologies = ['Node.js', 'React', 'MySQL', 'Express.js', 'JavaScript'];

const services = ['Application Engineering', 'Database Modernization', 'Web Development'];

const testimonial = {
  quote: 'FirstLogic transformed our outdated website into a modern, fast, and scalable platform. The migration was seamless, and the results exceeded our expectations.',
  author: 'Technoflex Team',
  role: 'Technoflex Management'
};

const TechnoflexWebModernizationPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      <section className="relative isolate overflow-hidden pt-36 pb-32">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Web Application & Database Modernization"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/65 via-slate-950/55 to-slate-950/65" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white dark:from-slate-950 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white drop-shadow-[0_4px_20px_rgba(15,23,42,0.4)]">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 dark:bg-slate-900/40 backdrop-blur-sm border border-white/40 dark:border-slate-700 mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <IconWrapper icon={HiStar} className="w-5 h-5 text-indigo-400 mr-2" />
                <span className="text-sm font-medium text-white">
                  Manufacturing Case Study
                </span>
              </motion.div>

              <motion.h1
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Web Application & Database Modernization
              </motion.h1>
              <motion.p
                className="text-lg text-white/75 mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Technoflex - Leading Manufacturer of Electrical Conduit Pipes & Accessories
              </motion.p>

              <motion.p
                className="text-xl text-white/85 mb-8 leading-relaxed"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                Modernizing legacy web application and database infrastructure for a leading electrical conduit manufacturing company using Node.js, React, and MySQL on shared hosting.
              </motion.p>

              <motion.div
                className="flex items-center space-x-6 mb-8"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                <div className="flex items-center">
                  <IconWrapper icon={HiUsers} className="w-5 h-5 text-indigo-400 mr-2" />
                  <span className="text-white/90 font-medium">
                    Technoflex
                  </span>
                </div>
                <div className="flex items-center">
                  <IconWrapper icon={HiBuildingOffice} className="w-5 h-5 text-indigo-400 mr-2" />
                  <span className="text-white/90 font-medium">
                    Manufacturing
                  </span>
                </div>
                <div className="flex items-center">
                  <IconWrapper icon={HiGlobeAlt} className="w-5 h-5 text-indigo-400 mr-2" />
                  <a 
                    href="https://www.technoflex.in/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white/90 font-medium hover:text-white underline"
                  >
                    technoflex.in
                  </a>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="relative w-full h-80 lg:h-[440px] rounded-3xl overflow-hidden shadow-2xl border border-white/30 bg-white/10 backdrop-blur-sm">
                <img
                  src={heroImage}
                  alt="Web Application & Database Modernization"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/30 to-slate-900/40" />
                <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-3">
                  {services.map(service => (
                    <span
                      key={service}
                      className="px-3 py-1 rounded-full text-xs font-semibold bg-white/90 dark:bg-slate-900/90 text-slate-800 dark:text-white backdrop-blur-sm"
                    >
                      {service}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {keyMetrics.map((metric, index) => (
              <motion.div
                key={metric.metric}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-6 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm"
              >
                <div className="text-sm uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                  {metric.metric}
                </div>
                <div className="text-3xl font-bold text-slate-900 dark:text-white">
                  {metric.value}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Key Results</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {results.map((result, index) => (
              <motion.div
                key={result.metric}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-6 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm text-center"
              >
                <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400 mb-2">
                  {result.value}
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-300">
                  {result.metric}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-6">
          <div className="p-8 rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Pain Points</h3>
            <ul className="space-y-3">
              {painPoints.map(point => (
                <li key={point} className="flex items-start text-slate-600 dark:text-slate-300">
                  <span className="mt-1 mr-3">
                    <IconWrapper icon={HiMiniSparkles} className="w-5 h-5 text-indigo-500" />
                  </span>
                  {point}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-8 rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Strategy</h3>
            <ol className="space-y-4 list-decimal list-inside text-slate-600 dark:text-slate-300">
              {strategy.map(step => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-slate-900 dark:text-white mb-8 text-center">
            Results & Impact
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {outcomes.map(outcome => (
              <motion.div
                key={outcome.title}
                className="p-6 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wide mb-2">
                  {outcome.title}
                </div>
                <p className="text-slate-600 dark:text-slate-300">{outcome.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-8">
            <div className="rounded-3xl border border-slate-200 dark:border-slate-800 p-8 bg-white dark:bg-slate-950 shadow-sm">
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Engagement Timeline</h3>
              <div className="space-y-6">
                {timeline.map(item => (
                  <div key={item.phase} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-200 font-semibold flex items-center justify-center">
                      {item.phase.split(' ')[0]}
                    </div>
                    <div>
                      <div className="text-sm uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        {item.duration}
                      </div>
                      <div className="text-lg font-semibold text-slate-900 dark:text-white">
                        {item.phase}
                      </div>
                      <p className="text-slate-600 dark:text-slate-300 text-sm">{item.focus}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 dark:border-slate-800 p-8 bg-white dark:bg-slate-950 shadow-sm">
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Technologies</h3>
              <div className="flex flex-wrap gap-3">
                {technologies.map(tech => (
                  <span
                    key={tech}
                    className="px-3 py-1.5 rounded-full text-sm bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-white"
                  >
                    {tech}
                  </span>
                ))}
              </div>
              <div className="mt-6 p-4 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800">
                <div className="flex items-center gap-2 mb-2">
                  <IconWrapper icon={HiGlobeAlt} className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  <span className="font-semibold text-slate-900 dark:text-white">Website</span>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">
                  Shared hosting deployment at
                </p>
                <a 
                  href="https://www.technoflex.in/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="font-mono text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 underline"
                >
                  www.technoflex.in
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center p-10 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-3xl text-slate-900 dark:text-white shadow-sm"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <IconWrapper icon={HiStar} className="w-12 h-12 text-indigo-500 mx-auto mb-6" />
            <blockquote className="text-2xl italic mb-8 leading-relaxed text-slate-700 dark:text-slate-200">
              "{testimonial.quote}"
            </blockquote>
            <div>
              <div className="font-semibold text-lg">{testimonial.author}</div>
              <div className="text-slate-500 dark:text-slate-400">{testimonial.role}</div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            className="relative p-10 border border-slate-200 dark:border-slate-800 rounded-3xl overflow-hidden bg-white dark:bg-slate-950"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-slate-100 dark:from-slate-900 dark:to-slate-950" />
            <div className="relative z-10">
              <motion.h2
                className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Ready to Start Your Engagement?
              </motion.h2>
              <motion.p
                className="text-lg text-slate-600 dark:text-slate-300 mb-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                Let's discuss how we can deliver the same outcomes for your teams.
              </motion.p>
              <motion.div
                className="flex flex-col sm:flex-row gap-4 justify-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
              >
                <motion.button
                  className="px-8 py-4 bg-indigo-600 text-white rounded-full font-semibold text-lg hover:bg-indigo-700 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => (window.location.href = '/contact')}
                >
                  Start Your Project
                </motion.button>
                <motion.button
                  className="px-8 py-4 bg-transparent border-2 border-indigo-600 dark:border-slate-700 text-indigo-600 dark:text-white rounded-full font-semibold text-lg hover:bg-indigo-50 dark:hover:bg-slate-900 transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => (window.location.href = '/contact')}
                >
                  Schedule Consultation
                </motion.button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TechnoflexWebModernizationPage;

